/**
 * Het formulier van één Mass DM: model, publiek, filters, tekst met emoji,
 * content uit de bibliotheek (gratis of betaald) en de vertaling vooraf.
 *
 * 03-10-2026 (Jordy): dit stond in `MassDmSendDialog.tsx`. De tab "Create" op
 * Mass Messages plant drie Mass Messages naast elkaar, met precies dezelfde
 * velden en regels. Om die regels niet twee keer te hebben (en ze uit elkaar
 * te laten lopen) staat het formulier nu hier, en gebruiken het venster en de
 * drie slots allebei:
 *
 *   - `MassDmWaarden` is de inhoud van één formulier, als gewone gegevens. De
 *     eigenaar (venster of slot) bewaart hem; zo kan een slot gewist of
 *     gekopieerd worden.
 *   - `massDmAfgeleid` rekent eruit wat ertoe doet (gekozen model, taal,
 *     betaald, minimumprijs, de velden voor de server).
 *   - `massDmFouten` zegt wat er nog ontbreekt vóór versturen of plannen.
 *   - `MassDmVelden` tekent de velden en haalt de vertaling vanzelf op.
 *
 * De methodekeuze, de knoppen en het plannen horen bij de eigenaar.
 */
import { MmAiVeldKnop } from './MmAiSuggesties'
import { useEffect, useRef, useState, type Ref } from 'react'
import { Field } from './Modal'
import { PREVIEW_TITEL, VerzendPreviewInhoud } from './VerzendPreview'
import { ContentPicker, type ContentKeuze } from './ContentPicker'
import { EmojiKnop } from './EmojiKnop'
import { viaWorker } from '../lib/massDmWorker'
import { api, fileUrl, thumbUrl } from '../lib/api'
import { credits } from '../lib/format'
import type { Persona } from '../pages/Models'
import { useMij } from '../lib/rechten'
import { magTestDoelgroep } from '../lib/massaRechten'
import { EnRegel } from './EnRegel'
import {
  bibliotheekVoorstel, eenheidVoor, isF2f, minimumVoor, ppvMinimumTekst, prijsAlsGetal, prijsFouten, prijsHulpTekst,
  prijsPerFanTekst,
} from '../lib/massPrijs'

// 04-10-2026: de prijsregels staan in lib/massPrijs.ts (ook voor de Agenda).
export { ppvMinimumTekst }

/** Wat de server terugmeldt op een dry run. Niet elk pad vult alles. */
export interface MassPreview {
  recipient_count: number | null
  known_recipient_count?: number | null
  platform_follower_count?: number | null
  unaddressable_follower_count?: number | null
  native_audience?: boolean | null
  audience_complete?: boolean | null
  excluded_recent_count?: number | null
  excluded_timewaster_count?: number | null
  excluded_spender_count?: number | null
  excluded_new_follower_count?: number | null
  excluded_private_count?: number | null
  excluded_already_delivered_count?: number | null
  sample_fan_ids?: string[] | null
}

/** De regels onder "Filtered out", zonder de nullen. */
export function uitsluitingen(p: MassPreview): { label: string; aantal: number }[] {
  const regels = [
    { label: 'Replied recently', aantal: p.excluded_recent_count ?? 0 },
    { label: 'Timewasters', aantal: p.excluded_timewaster_count ?? 0 },
    { label: 'Paying fans', aantal: p.excluded_spender_count ?? 0 },
    { label: 'New followers', aantal: p.excluded_new_follower_count ?? 0 },
    { label: 'Private chats', aantal: p.excluded_private_count ?? 0 },
    { label: 'Already delivered', aantal: p.excluded_already_delivered_count ?? 0 },
  ]
  return regels.filter((r) => r.aantal > 0)
}

/** Het bereik zoals het op het scherm komt te staan. */
export function bereikTekst(p: MassPreview): string {
  if (p.recipient_count != null) return `${p.recipient_count} recipients`
  // Een volledige Platform Mass DM: het platform bepaalt de doelgroep zelf.
  const bekend = p.known_recipient_count ?? 0
  return `The platform decides the audience (at least ${bekend} known contacts)`
}

/** Eén regel in de modellijst: hetzelfde model kan op twee platforms staan. */
export interface ModelKeuze {
  sleutel: string
  persona_id: number
  slug: string
  platform: 'fancentro' | 'f2f'
  label: string
  /** 03-10-2026: voor de modelfoto in een slot van "Create". */
  avatar_url?: string | null
}

/**
 * De modellijst waarin elk platform een eigen regel is.
 *
 * 18-09-2026 (Jordy): "AlicelMonta" stond twee keer in de lijst zonder dat je
 * zag welke de FanCentro- en welke de F2F-versie was, met daarnaast losse
 * platformknoppen. Het platform zit nu in de regel zelf; de losse knop is weg.
 * De slug bepaalt het platform — die begint altijd met `fc_` of `f2f_`.
 */
export function modelKeuzes(personas: Persona[]): ModelKeuze[] {
  const uit: ModelKeuze[] = []
  for (const p of personas) {
    const slug = String(p.slug || '')
    const platform = slug.startsWith('f2f_') ? 'f2f' : slug.startsWith('fc_') ? 'fancentro' : null
    if (!platform) continue
    const naam = p.stage_name || p.name || slug
    uit.push({
      sleutel: `${platform}:${slug}`,
      persona_id: p.id,
      slug,
      platform,
      label: `${naam} — ${platform === 'f2f' ? 'F2F' : 'FanCentro'}`,
      avatar_url: p.avatar_url,
    })
  }
  return uit.sort((a, b) => a.label.localeCompare(b.label))
}

/** De taal waarin een fan van dit platform het bericht hoort te krijgen. */
export function doeltaalVoor(platform: string): { code: string; naam: string } | null {
  // F2F is een Nederlands platform: daar ontvangen fans Nederlands terwijl de
  // chatter Engels typt. Op FanCentro gaat de tekst zoals getypt.
  return platform === 'f2f' ? { code: 'nl', naam: 'Dutch' } : null
}

export const DOELGROEPEN = [
  { value: 'all', label: 'All addressable fans' },
  { value: 'spenders', label: 'Paying fans only' },
  { value: 'new_followers', label: 'New followers only' },
  { value: 'timewasters', label: 'Timewasters only' },
  { value: 'maybe_timewasters', label: 'Maybe timewasters only' },
  { value: 'waiting', label: 'Waiting queue only' },
  { value: 'new_subscriptions', label: 'New subscriptions only (7 days)' },
]

/** 21-10-2026 (Jordy): fans die ZELF de laatste X tijd iets stuurden overslaan (fan-activiteit). */
export const FAN_ACTIEF = [
  { value: 0, label: 'Don’t exclude' },
  { value: 60, label: 'Active in the last hour' },
  { value: 1440, label: 'Active in the last 24 hours' },
]

export const RECENT = [
  { value: 0, label: 'Don’t exclude (include recent replies)' },
  { value: 120, label: 'Last 2 hours' },
  { value: 360, label: 'Last 6 hours' },
  { value: 720, label: 'Last 12 hours' },
  { value: 1440, label: 'Last 24 hours' },
]

export type Methode = 'direct' | 'native'

/** De uitsluitingen (vinkjes) van een Mass DM; 21-10-2026 ook gebruikt door Promo Auto MM. */
export const UITSLUITINGEN: [string, 'geenTimewasters' | 'geenMaybe' | 'geenBetalers' | 'geenNieuwe' | 'geenPrive' | 'alleenOnline'][] = [
  ['Exclude timewasters', 'geenTimewasters'],
  ['Exclude maybe timewasters', 'geenMaybe'],
  ['Exclude paying fans', 'geenBetalers'],
  ['Exclude new followers', 'geenNieuwe'],
  ['Exclude private chats', 'geenPrive'],
  ['Online fans only', 'alleenOnline'],
]

/** De naam van een methode, zoals in de methodekeuze en de historie. */
export function methodeNaam(methode: Methode | null): string {
  return methode === 'native' ? 'Platform Mass DM' : 'Dashboard Mass Message'
}

/** De inhoud van één Mass DM-formulier. */
export interface MassDmWaarden {
  methode: Methode | null
  modelSleutel: string
  publiek: 'test' | 'echt'
  tekst: string
  vertaal: boolean
  doelgroep: string
  recent: number
  /** 21-10-2026: minuten; fans die zelf zo recent iets stuurden worden overgeslagen. */
  fanActief: number
  geenTimewasters: boolean
  /** 21-10-2026 (Jordy): maybe-timewasters weglaten. */
  geenMaybe?: boolean
  geenBetalers: boolean
  geenNieuwe: boolean
  geenPrive: boolean
  alleenOnline: boolean
  // 01-10-2026 (Jordy): foto's/video's uit de bibliotheek, gratis of betaald.
  media: ContentKeuze | null
  // 05-10-2026 (Jordy): "Send free / Paid unlock" en de prijs staan los van de
  // content, altijd zichtbaar. Kiezen, wisselen of weghalen van content laat
  // ze staan. Zonder content mag "Paid unlock" alleen als concept bewaard.
  betaald: boolean
  prijs: string
  // 03-10-2026 (Jordy): de vertaling vóór het versturen laten zien, en laten
  // aanpassen. Hij komt vanzelf (kort na het typen) en staat in een bewerkbaar
  // vak. `bron` is het Engels waarvan deze vertaling is gemaakt. Wat in het vak
  // staat gaat als `delivery_text_override` mee: precies die tekst krijgen de
  // fans, de server vertaalt dan niet opnieuw. Is er (nog) geen actuele
  // vertaling, dan vertaalt de server zoals voorheen.
  // Wie het Nederlands met de hand aanpaste en daarna het Engels wijzigt,
  // krijgt de keuze (opnieuw vertalen of deze tekst houden); tot die keuze gaat
  // er niets uit, zodat een handmatige aanpassing nooit stil verdwijnt.
  vertaling: { bron: string; tekst: string } | null
  aangepast: boolean
  vertaalBezig: boolean
  vertaalFout: string
}

/**
 * 21-10-2026 (Jordy): een segment (niet "all") of een uitsluiting gekozen? Dan gaat de MM
 * altijd DIRECT (alleen daar werken de filters); de server dwingt hetzelfde af.
 */
export function heeftFilters(w: Pick<MassDmWaarden, 'doelgroep' | 'recent' | 'fanActief' | 'geenTimewasters'
  | 'geenMaybe' | 'geenBetalers' | 'geenNieuwe' | 'geenPrive' | 'alleenOnline'>): boolean {
  return (w.doelgroep || 'all') !== 'all' || w.recent > 0 || (w.fanActief ?? 0) > 0 || w.geenTimewasters
    || Boolean(w.geenMaybe) || w.geenBetalers || w.geenNieuwe || w.geenPrive || w.alleenOnline
}

export function leegMassDm(methode: Methode | null = null): MassDmWaarden {
  return {
    methode,
    modelSleutel: '',
    publiek: 'test',
    tekst: '',
    vertaal: true,
    doelgroep: 'all',
    recent: 0,
    fanActief: 0,
    geenTimewasters: false,
    geenMaybe: false,
    geenBetalers: false,
    geenNieuwe: false,
    geenPrive: false,
    alleenOnline: false,
    media: null,
    betaald: false,
    prijs: '',
    vertaling: null,
    aangepast: false,
    vertaalBezig: false,
    vertaalFout: '',
  }
}

export type ZetMassDm = (patch: Partial<MassDmWaarden>) => void

/** Alles wat uit de waarden volgt, op één plek berekend. */
export function massDmAfgeleid(w: MassDmWaarden, keuzes: ModelKeuze[], magBetaald: boolean) {
  const { methode: gekozenMethode, modelSleutel, publiek, tekst, vertaal, media, vertaling, aangepast } = w
  const methode: Methode | null = heeftFilters(w) ? 'direct' : gekozenMethode
  const gekozen = keuzes.find((k) => k.sleutel === modelSleutel) || null
  const taal = gekozen ? doeltaalVoor(gekozen.platform) : null
  const naarEchteFans = publiek === 'echt'
  const vertalerAan = Boolean(taal) && vertaal
  const vertalingNodig = vertalerAan && Boolean(tekst.trim())
  const vertalingActueel = Boolean(
    vertaling && vertaling.bron === tekst.trim() && vertaling.tekst.trim(),
  )
  const keuzeNodig = vertalingNodig && Boolean(vertaling) && !vertalingActueel && aangepast
  const overschrijf = vertalingNodig && vertalingActueel ? vertaling!.tekst.trim() : ''
  const metMedia = Boolean(media && media.items.length)
  // 05-10-2026 (Jordy): de keuze staat los van de content. `gekozenBetaald` is
  // wat er in het formulier staat; `betaaldMedia` wat er echt betaald uitgaat.
  const gekozenBetaald = magBetaald && Boolean(w.betaald)
  const betaaldMedia = metMedia && gekozenBetaald
  const betaaldZonderContent = gekozenBetaald && !metMedia
  // 04-10-2026 (Jordy): de prijs is nu ook in het slot (en in het venster) zelf
  // in te vullen. Leeg of onzin telt als 0; `prijsMeldingen` zegt wat er mis is.
  const prijsGetal = prijsAlsGetal(w.prijs) || 0
  // 01-10-2026: F2F weigert een Platform Mass DM zonder tekst, ook met media.
  const tekstVerplicht = gekozen?.platform === 'f2f' && methode === 'native'
  // 01-10-2026 (Jordy): minimumprijs voor een betaalde Mass DM. De kiezer laat
  // een lagere prijs al niet toe; dit is de vangrail voor als het model of het
  // platform na het kiezen wisselt.
  const ppvMinimum = minimumVoor(gekozen?.platform)
  const ppvTeLaag = gekozenBetaald && prijsGetal > 0 && prijsGetal < ppvMinimum
  const prijsMeldingen = prijsFouten(gekozen?.platform, gekozenBetaald, w.prijs)
  /** Wat één fan betaalt, voor slot, preview en bevestiging. */
  const prijsRegelTekst = prijsPerFanTekst(gekozen?.platform, gekozenBetaald, w.prijs,
                                           metMedia ? media!.items.length : 0)
  const basis = {
    model_persona_id: gekozen?.persona_id ?? 0,
    model_slug: gekozen?.slug ?? '',
    platform: gekozen?.platform ?? '',
    content: tekst,
    fc_delivery_mode: methode ?? 'native',
    only_test: !naarEchteFans,
    exclude_test: naarEchteFans,
    // Doelgroep en uitsluitingen, precies de velden die de server kent.
    audience_include: w.doelgroep,
    exclude_recent_minutes: w.recent,
    exclude_fan_active_minutes: w.fanActief ?? 0,
    exclude_timewasters: w.geenTimewasters,
    exclude_maybe_timewasters: Boolean(w.geenMaybe),
    exclude_spenders: w.geenBetalers,
    exclude_new_followers: w.geenNieuwe,
    exclude_private_chats: w.geenPrive,
    online_only: w.alleenOnline,
    // Vertalen: alleen zinvol als dit platform een eigen taal heeft.
    translation_enabled: Boolean(taal) && vertaal,
    ...(taal && vertaal ? { translate_to: taal.code } : {}),
    // 03-10-2026: de bekeken (en eventueel aangepaste) vertaling.
    ...(overschrijf ? { delivery_text_override: overschrijf } : {}),
    // 05-10-2026: betaald zonder content gaat mee, zodat de server het weigert.
    ...(betaaldZonderContent ? { is_paid: true } : {}),
  }
  return {
    gekozen, taal, naarEchteFans, vertalingNodig, vertalingActueel, keuzeNodig,
    overschrijf, metMedia, betaaldMedia, gekozenBetaald, betaaldZonderContent, prijsGetal, tekstVerplicht, ppvMinimum,
    ppvTeLaag, prijsMeldingen, prijsRegelTekst, basis,
  }
}

export type MassDmAfgeleid = ReturnType<typeof massDmAfgeleid>

/**
 * Wat er nog ontbreekt voor dit formulier verstuurd of gepland kan worden.
 * Leeg = in orde. De getypte SEND bij echte fans valt hierbuiten; die vraagt
 * de eigenaar zelf, op het moment van versturen of plannen.
 */
/** 05-10-2026 (Jordy): bij het slot en (400) op de server. */
export const BETAALD_ZONDER_CONTENT = 'Choose content for a paid message.'

export function massDmFouten(w: MassDmWaarden, a: MassDmAfgeleid, opties: { concept?: boolean } = {}): string[] {
  const { gekozen, metMedia, tekstVerplicht, ppvTeLaag, vertalingNodig, keuzeNodig, taal } = a
  const tekst = w.tekst
  const fouten: string[] = []
  if (!gekozen) fouten.push('Choose a model.')
  else if (!(tekst.trim() || (metMedia && !tekstVerplicht))) {
    fouten.push(tekstVerplicht && metMedia
      ? 'F2F needs a message with a Platform Mass DM, also with a photo or video.'
      : 'Write a message or add a photo or video.')
  }
  // Een concept mag "Paid unlock" zonder content bewaren; plannen en versturen niet.
  if (a.betaaldZonderContent && !opties.concept) fouten.push(BETAALD_ZONDER_CONTENT)
  if (ppvTeLaag) fouten.push(`${ppvMinimumTekst(gekozen?.platform)}.`)
  // 04-10-2026: de overige prijsregels (leeg, geen hele credits, te hoog).
  else fouten.push(...a.prijsMeldingen)
  // 03-10-2026: niet versturen terwijl de vertaling nog komt (dan zou de
  // server een andere vertaling maken dan je zo meteen ziet), en niet zolang
  // een handmatig aangepaste vertaling niet meer bij het Engels past.
  if (vertalingNodig && w.vertaalBezig) fouten.push('Wait for the translation.')
  if (keuzeNodig) {
    fouten.push(`Translate again, or keep the edited ${taal?.naam ?? 'translated'} text.`)
  }
  return fouten
}

/**
 * Het verzoek voor preview of versturen. Met media gaat het als formulier
 * naar /mass-media (dezelfde velden, plus de gekozen content en de prijs),
 * anders als JSON naar /mass-message.
 */
export function mediaFormulier(w: MassDmWaarden, a: MassDmAfgeleid, extra: Record<string, string>): FormData {
  const { basis, betaaldMedia, overschrijf } = a
  const f = new FormData()
  f.set('model_persona_id', String(basis.model_persona_id))
  f.set('model_slug', basis.model_slug)
  f.set('platform', basis.platform)
  f.set('caption', w.tekst)
  f.set('content_ids', (w.media?.items ?? []).map((it) => it.id).join(','))
  f.set('is_paid', String(betaaldMedia))
  f.set('price', betaaldMedia ? String(a.prijsGetal) : '0')
  f.set('fc_delivery_mode', basis.fc_delivery_mode)
  f.set('only_test', String(basis.only_test))
  f.set('exclude_test', String(basis.exclude_test))
  f.set('audience_include', basis.audience_include)
  f.set('exclude_recent_minutes', String(basis.exclude_recent_minutes))
  f.set('exclude_fan_active_minutes', String(basis.exclude_fan_active_minutes))
  f.set('exclude_timewasters', String(basis.exclude_timewasters))
  f.set('exclude_maybe_timewasters', String(basis.exclude_maybe_timewasters))
  f.set('exclude_spenders', String(basis.exclude_spenders))
  f.set('exclude_new_followers', String(basis.exclude_new_followers))
  f.set('exclude_private_chats', String(basis.exclude_private_chats))
  f.set('online_only', String(basis.online_only))
  f.set('translation_enabled', String(basis.translation_enabled))
  f.set('translate_to', basis.translate_to ?? '')
  if (overschrijf) f.set('delivery_text_override', overschrijf)
  for (const [k, v] of Object.entries(extra)) f.set(k, v)
  return f
}

/** Preview (dry run) of versturen. Beide lopen via de worker. */
export function massVerzoek<T>(w: MassDmWaarden, a: MassDmAfgeleid, dryRun: boolean): Promise<T> {
  return a.metMedia
    ? viaWorker<T>('/api/admin/conversations/mass-media', mediaFormulier(w, a, { dry_run: String(dryRun) }))
    : viaWorker<T>('/api/admin/conversations/mass-message', { ...a.basis, dry_run: dryRun })
}

/**
 * De velden van één Mass DM. `onWijzig` hoort elke wijziging: 'alles' maakt
 * een preview en de getypte bevestiging ongeldig, 'bevestiging' alleen de
 * bevestiging (een nieuwe of aangepaste vertaling verandert het aantal niet).
 */
export function MassDmVelden({
  w,
  zet,
  keuzes,
  magBetaald,
  platformFilter = 'all',
  onWijzig,
  echteFansHint = 'Real fans. Type SEND below to unlock the send button.',
  aiSuggesties = false,
}: {
  w: MassDmWaarden
  zet: ZetMassDm
  keuzes: ModelKeuze[]
  magBetaald: boolean
  /** Het platformfilter van de pagina: 'all' | 'fancentro' | 'f2f'. */
  platformFilter?: string
  onWijzig?: (soort: 'alles' | 'bevestiging') => void
  /** Uitleg bij "Real fans": waar de getypte SEND gevraagd wordt. */
  echteFansHint?: string
  /** 07-10-2026: knop "Suggest texts" bij het berichtveld (alleen op de Mass Messages-pagina). */
  aiSuggesties?: boolean
}) {
  const a = massDmAfgeleid(w, keuzes, magBetaald)
  const {
    gekozen, taal, naarEchteFans, metMedia, betaaldMedia, ppvTeLaag, tekstVerplicht,
    vertalingNodig, vertalingActueel, keuzeNodig,
  } = a
  const { methode: gekozenMethode, tekst, vertaal, vertaling, vertaalBezig, vertaalFout, media } = w
  const gefilterd = heeftFilters(w)
  const methode: Methode | null = gefilterd ? 'direct' : gekozenMethode
  const [kiezerOpen, setKiezerOpen] = useState(false)
  // 04-10-2026 (Jordy): "Test accounts only" alleen voor owner en admin; voor
  // iedereen anders is de doelgroep altijd Real fans (de server weigert test).
  const mijData = useMij().data
  const magTest = magTestDoelgroep(mijData)
  useEffect(() => {
    if (mijData && !magTest && w.publiek === 'test') zet({ publiek: 'echt' })
  }, [mijData, magTest, w.publiek, zet])

  /** Een wijziging die ook de preview ongeldig maakt. */
  const verander = (patch: Partial<MassDmWaarden>) => {
    onWijzig?.('alles')
    zet(patch)
  }

  // 04-10-2026 (Jordy): gratis/betaald en de prijs in het formulier zelf, niet
  // alleen in de kiezer. Het aantal ontvangers verandert er niet door, de
  // bevestiging (SEND) wel. De getypte prijs blijft bewaard als je even op
  // Free zet; er gaat dan 0 mee (zie `betaaldMedia`).
  const f2f = isF2f(gekozen?.platform)
  const voorstel = bibliotheekVoorstel(media?.items, gekozen?.platform)
  const zetPrijs = (patch: { betaald?: boolean; prijs?: string }) => {
    onWijzig?.('bevestiging')
    zet(patch)
  }
  const kiesBetaald = () => {
    // De bibliotheekprijs is een voorstel, alleen als er nog niets staat.
    const leeg = !String(w.prijs ?? '').trim()
    zetPrijs({ betaald: true, prijs: leeg && voorstel != null ? String(voorstel) : w.prijs })
  }

  // 01-10-2026 (Jordy): emoji's in de tekst, op de plek van de cursor.
  const tekstVak = useRef<HTMLTextAreaElement>(null)
  function voegEmojiIn(emoji: string) {
    const vak = tekstVak.current
    const begin = vak?.selectionStart ?? tekst.length
    const eind = vak?.selectionEnd ?? tekst.length
    verander({ tekst: tekst.slice(0, begin) + emoji + tekst.slice(eind) })
    // De cursor direct achter de emoji, zodat je verder kunt typen.
    window.requestAnimationFrame(() => {
      if (!vak) return
      vak.focus()
      vak.setSelectionRange(begin + emoji.length, begin + emoji.length)
    })
  }

  // Een antwoord dat binnenkomt nadat dit formulier weg is (slot gewist of
  // venster dicht), telt niet.
  const actief = useRef(true)
  useEffect(() => {
    actief.current = true
    return () => { actief.current = false }
  }, [])
  const laatsteVraag = useRef('')

  async function vraagVertaling() {
    if (!gekozen || !taal) return
    const bron = tekst.trim()
    if (!bron) return
    laatsteVraag.current = bron
    zet({ vertaalBezig: true, vertaalFout: '' })
    const geldig = () => actief.current && laatsteVraag.current === bron
    try {
      const r = await api.post<{ translation: string }>(
        '/api/admin/conversations/mass-message/translate-preview',
        {
          text: bron,
          platform: gekozen.platform,
          fc_delivery_mode: methode ?? 'native',
          translation_enabled: true,
          translate_to: taal.code,
        },
      )
      // Een antwoord op een oudere vraag (er is intussen verder getypt) telt niet.
      if (!geldig()) return
      const nl = String(r?.translation ?? '').trim()
      if (!nl) throw new Error('No translation came back.')
      zet({ vertaling: { bron, tekst: nl }, aangepast: false })
      onWijzig?.('bevestiging')
    } catch (e) {
      if (geldig()) {
        zet({ vertaalFout: `Translation preview: ${e instanceof Error ? e.message : String(e)}` })
      }
    } finally {
      if (geldig()) zet({ vertaalBezig: false })
    }
  }

  // Vanzelf vertalen, kort nadat het typen stopt. Niet bij een handmatig
  // aangepaste vertaling (dan eerst de keuze) en niet als hij al klopt.
  useEffect(() => {
    if (!vertalingNodig || !gekozen || vertalingActueel || keuzeNodig) return
    if (laatsteVraag.current === tekst.trim() && (vertaalBezig || vertaalFout)) return
    const t = window.setTimeout(() => { void vraagVertaling() }, 900)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tekst, vertalingNodig, gekozen?.sleutel, vertalingActueel, keuzeNodig])

  return (
    <>
      <Field
        label="Model"
        hint="Each platform is its own line — the same model can run on both."
      >
        <select className="inp" value={w.modelSleutel}
                onChange={(e) => verander({ media: null, modelSleutel: e.target.value })}>
          <option value="">Choose a model…</option>
          {keuzes
            .filter((k) => platformFilter === 'all' || k.platform === platformFilter)
            .map((k) => (
              <option key={k.sleutel} value={k.sleutel}>{k.label}</option>
            ))}
        </select>
      </Field>

      <Field
        label="Audience"
        hint={naarEchteFans
          ? echteFansHint
          : 'Test accounts only — safe trial of the send loop.'}
      >
        <select className="inp" value={w.publiek}
                onChange={(e) => verander({ publiek: e.target.value as 'test' | 'echt' })}>
          {magTest ? <option value="test">Test accounts only (safe)</option> : null}
          <option value="echt">Real fans</option>
        </select>
      </Field>

      {/* 21-10-2026 (Jordy): segment en uitsluitingen altijd kiesbaar; een keuze maakt het een directe MM. */}
      <Field label="Send to">
        <select className="inp" value={w.doelgroep} aria-label="Send to"
                onChange={(e) => verander({ doelgroep: e.target.value })}>
          {DOELGROEPEN.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
      </Field>
      <Field label="Exclude fans active (their own messages)">
        <select className="inp" value={String(w.fanActief ?? 0)} aria-label="Exclude fans active"
                onChange={(e) => verander({ fanActief: Number(e.target.value) })}>
          {FAN_ACTIEF.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </Field>
      <Field label="Exclude recent replies (ours)">
        <select className="inp" value={String(w.recent)}
                onChange={(e) => verander({ recent: Number(e.target.value) })}>
          {RECENT.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </Field>
      <div className="field">
        <label>Exclusions</label>
        <div className="uitsluitingen">
          {UITSLUITINGEN
            .map(([label, veld]) => (
              <label className="chkrow" key={label}>
                <span className={w[veld] ? 'chk on' : 'chk'}
                      onClick={() => verander({ [veld]: !w[veld] } as Partial<MassDmWaarden>)}>{w[veld] ? '✓' : ''}</span>
                {label}
              </label>
            ))}
        </div>
      </div>
      <div className="field" data-testid="mm-filter-afweging">
        {gefilterd ? (
          <span className="note">
            Filtered Mass Message: sent <b>directly, one by one</b> (Dashboard Mass Message). It reaches
            <b> only fans who have a conversation</b>, not all followers, and takes about 2 seconds per fan.
            {gekozenMethode === 'native' ? ' The Platform Mass DM is switched off for this message because of the filter.' : ''}
          </span>
        ) : (
          <span className="hint">
            {gekozenMethode === 'native'
              ? 'No segment or exclusion: Platform Mass DM to the whole follower group (also fans who never chatted). Choosing a filter above makes it a direct message.'
              : 'No segment or exclusion: sent to all fans who have a conversation.'}
          </span>
        )}
      </div>

      <Field
        label={tekstVerplicht ? 'Message' : metMedia ? 'Message (optional with content)' : 'Message'}
        hint={tekstVerplicht && metMedia && !tekst.trim()
          ? 'F2F needs a message with a Platform Mass DM, also with a photo or video.'
          : undefined}
      >
        <textarea ref={tekstVak} className="inp" rows={5} value={tekst}
                  onChange={(e) => verander({ tekst: e.target.value })} />
        {/* 24-10-2026 (Jordy): Engelse regel ter info als de tekst niet Engels is (Create en Edit). */}
        <EnRegel tekst={tekst} wacht={800} />
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 4, gap: 6 }}>
          {aiSuggesties ? (
            <MmAiVeldKnop
              model={gekozen ? { slug: gekozen.slug, platform: gekozen.platform, label: gekozen.label } : null}
              // Alleen het berichtveld. F2F-voorstellen zijn al Nederlands: niet nog eens vertalen.
              onGebruik={(t) => verander(gekozen?.platform === 'f2f' ? { tekst: t, vertaal: false } : { tekst: t })}
            />
          ) : null}
          <EmojiKnop uitlijnen="rechts" onKies={voegEmojiIn} />
        </div>
      </Field>

      {/* 01-10-2026 (Jordy): foto's en video's meesturen, gratis of als
          betaalde unlock (PPV) — met dezelfde bibliotheekkiezer als de chat. */}
      <Field
        label="Content (optional)"
        hint={!gekozen
          ? 'Choose a model first — the content comes from that model’s library.'
          : metMedia
            ? (betaaldMedia
              // 02-10-2026 (Jordy): FanCentro in credits ("4,000 credits"), F2F in euro's.
              ? `Paid unlock: ${gekozen.platform === 'f2f' ? `€${w.prijs}` : credits(w.prijs)}. The fan pays to open it.`
              : 'Sent free with the message.')
            : 'Up to 6 photos or videos from the library, free or paid.'}
      >
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {(media?.items ?? []).map((it) => (
            <img key={it.id} src={thumbUrl(it.id)} alt={it.title} title={it.title}
                 style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8 }} />
          ))}
          <button className="btn sm" type="button" disabled={!gekozen}
                  onClick={() => setKiezerOpen(true)}>
            {metMedia ? 'Change content' : 'Add photo / video'}
          </button>
          {metMedia ? (
            <button className="btn ghost sm" type="button"
                    onClick={() => verander({ media: null })}>
              Remove content
            </button>
          ) : null}
          {metMedia ? (
            <span className={betaaldMedia ? 'tag acc' : 'tag mut'}>
              {betaaldMedia ? 'paid' : 'free'} · {media?.items.length} item{media?.items.length === 1 ? '' : 's'}
            </span>
          ) : null}
        </div>
        {/* 04-10-2026 (Jordy): "Prijs duidelijk zichtbaar in het slot … zelf
            in te vullen, of aan te geven dat het gratis is." Eén prijs per
            fan voor de hele bundel (zie lib/massPrijs.ts). */}
        {/* 05-10-2026 (Jordy): altijd zichtbaar, ook zonder content. */}
        {(
          <div className="massdm-prijs" data-testid="massdm-prijs">
            {magBetaald ? (
              <div className="seg" role="group" aria-label="Free or paid">
                <button type="button" className={a.gekozenBetaald ? undefined : 'on'} aria-pressed={!a.gekozenBetaald}
                        onClick={() => zetPrijs({ betaald: false })}>
                  Send free
                </button>
                <button type="button" className={a.gekozenBetaald ? 'on' : undefined} aria-pressed={a.gekozenBetaald}
                        onClick={kiesBetaald}>
                  Paid unlock
                </button>
              </div>
            ) : (
              <span className="mut2" style={{ fontSize: 12 }}>
                Free only — paid Mass DMs aren’t enabled for your account.
              </span>
            )}
            {a.gekozenBetaald ? (
              <span className="row massdm-prijsveld" style={{ gap: 6 }}>
                {f2f ? <span className="mut2">€</span> : null}
                <input className="inp" type="number" inputMode={f2f ? 'decimal' : 'numeric'}
                       min={a.ppvMinimum} step={f2f ? '0.01' : '1'}
                       aria-label={`Price per fan (${eenheidVoor(gekozen?.platform)})`}
                       placeholder={`min ${a.ppvMinimum}`}
                       value={w.prijs ?? ''}
                       onChange={(e) => zetPrijs({ prijs: e.target.value })} />
                {f2f ? null : <span className="mut2">credits</span>}
              </span>
            ) : null}
            {/* 11-10-2026 (Jordy): dezelfde uitleg als in de contentkiezer —
                eenheid, omrekening en minimum. Alleen tekst; de regels zelf
                staan hierboven (prijsFouten / ppvTeLaag) en blijven gelijk. */}
            {a.gekozenBetaald ? (
              <span className="mut2" data-testid="massdm-prijs-hulp" style={{ fontSize: 11.5 }}>
                {prijsHulpTekst(gekozen?.platform)}
              </span>
            ) : null}
            <span className={a.gekozenBetaald ? 'tag acc' : 'tag mut'} data-testid="prijs-per-fan">
              {a.prijsRegelTekst}
            </span>
            {a.betaaldZonderContent ? (
              <span className="tag bad" data-testid="betaald-zonder-content">{BETAALD_ZONDER_CONTENT}</span>
            ) : null}
            {betaaldMedia && voorstel != null && prijsAlsGetal(w.prijs) === voorstel ? (
              <span className="mut2" style={{ fontSize: 11.5 }}>Library price (suggestion) — overwrite it if you like.</span>
            ) : null}
            {/* De minimumprijs (en de andere prijsregels) bij het prijsveld zelf. */}
            {ppvTeLaag ? (
              <span className="tag bad">{ppvMinimumTekst(gekozen?.platform)}</span>
            ) : a.gekozenBetaald && a.prijsMeldingen.length ? (
              <span className="tag bad">{a.prijsMeldingen[0]}</span>
            ) : null}
            {betaaldMedia && media && media.items.length > 1 ? (
              <span className="mut2" style={{ fontSize: 11.5 }}>
                A fan pays this once and unlocks all {media.items.length} items.
              </span>
            ) : null}
          </div>
        )}
      </Field>

      {/* De vertaler, zoals in de chat: je typt Engels en de fan krijgt zijn
          eigen taal. Alleen zichtbaar waar het platform een eigen taal heeft. */}
      {taal ? (
        <div className="field">
          <label className="chkrow">
            <span className={vertaal ? 'chk on' : 'chk'}
                  onClick={() => verander({ vertaal: !vertaal })}>{vertaal ? '✓' : ''}</span>
            Translate to {taal.naam}
          </label>
          <span className="hint">
            {vertaal
              ? `Type English — fans receive ${taal.naam}.`
              : 'Fans receive the text exactly as typed.'}
          </span>
        </div>
      ) : null}

      {/* 03-10-2026 (Jordy): wat de fans krijgen, vóór het versturen, en aan
          te passen. Precies deze tekst gaat uit en komt in de historie. */}
      {taal && vertalingNodig ? (
        <Field
          label={`Fans receive (${taal.code.toUpperCase()})`}
          hint={vertaalFout
            ? <span style={{ color: '#fca5a5' }}>{vertaalFout} Sending still translates on the server.</span>
            : !vertaling
              ? (vertaalBezig ? 'Translating…' : `The ${taal.naam} text appears here shortly after you stop typing.`)
              : vertalingActueel
                ? 'This exact text goes out. Edit it if something sounds off.'
                : keuzeNodig
                  ? <span style={{ color: '#fbbf24' }}>
                      You changed the English after editing the {taal.naam}. Translate again, or keep this {taal.naam} text.
                    </span>
                  : 'Updating the translation…'}
        >
          {vertaling ? (
            <textarea className="inp" rows={4} value={vertaling.tekst}
                      aria-label={`Fans receive (${taal.naam})`}
                      onChange={(e) => {
                        onWijzig?.('bevestiging')
                        zet({ aangepast: true, vertaling: { bron: vertaling.bron, tekst: e.target.value } })
                      }} />
          ) : null}
          <div className="row" style={{ gap: 8, marginTop: 4 }}>
            <button className="btn sm" type="button"
                    disabled={!gekozen || vertaalBezig || !tekst.trim()}
                    onClick={() => { void vraagVertaling() }}>
              {vertaalBezig ? 'Translating…' : vertaling ? 'Translate again' : 'Translate'}
            </button>
            {keuzeNodig && vertaling?.tekst.trim() ? (
              <button className="btn ghost sm" type="button"
                      onClick={() => zet({ vertaling: { bron: tekst.trim(), tekst: vertaling!.tekst } })}>
                Keep this text
              </button>
            ) : null}
          </div>
        </Field>
      ) : null}

      {kiezerOpen && gekozen ? (
        <ContentPicker
          modelSlug={gekozen.slug}
          platform={gekozen.platform}
          onClose={() => setKiezerOpen(false)}
          kiesModus={{
            // /mass-media neemt er maximaal zes mee, op beide platforms.
            max: 6,
            // 05-10-2026: de kiezer begint met de keuze en prijs van het formulier;
            // wat er na het kiezen staat, blijft staan (ook bij wisselen).
            begin: { items: media?.items ?? [], betaald: w.betaald, prijs: w.prijs },
            magBetaald,
            onKies: (keuze) => verander({ media: keuze, betaald: keuze.betaald, prijs: keuze.prijs }),
          }}
        />
      ) : null}
    </>
  )
}

/** De uitkomst van "Preview count", of de uitleg wat die knop doet. */
export function MassDmTelling({ preview }: { preview: MassPreview | null }) {
  if (!preview) {
    return (
      <div className="field">
        <span className="hint">
          Optional: “Preview count” shows how many fans this reaches. It sends
          nothing.
        </span>
      </div>
    )
  }
  return (
    <div className="field">
      <label>Preview</label>
      <div className="mut2" style={{ fontSize: 12.5, lineHeight: 1.7 }}>
        <div><b>{bereikTekst(preview)}</b></div>
        {preview.audience_complete === false ? (
          <div style={{ color: '#fbbf24' }}>
            The platform did not return a complete chat list; a real send is refused.
          </div>
        ) : null}
        {uitsluitingen(preview).length ? (
          <div>
            Filtered out:{' '}
            {uitsluitingen(preview).map((r) => `${r.label} ${r.aantal}`).join(' · ')}
          </div>
        ) : (
          <div>Filtered out: none</div>
        )}
        {preview.sample_fan_ids?.length ? (
          <div className="mut">Sample: {preview.sample_fan_ids.slice(0, 5).join(', ')}</div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Hoe het bericht bij de fan aankomt (dezelfde weergave als de chatkiezer).
 *
 * 01-10-2026: de preview staat IN het venster, niet als tweede venster
 * erbovenop. Dat tweede venster legde een laag over de knoppen eronder:
 * zichtbaar, maar een klik deed niets ("Preview count doet niets").
 */
export function MassDmBerichtVoorbeeld({
  w,
  a,
  preview,
  onSluit,
  plek,
}: {
  w: MassDmWaarden
  a: MassDmAfgeleid
  preview: MassPreview | null
  onSluit: () => void
  plek?: Ref<HTMLDivElement>
}) {
  const { gekozen, taal, overschrijf, betaaldMedia } = a
  return (
    <div ref={plek} className="card" style={{ marginTop: 12, background: 'var(--panel2)' }}>
      <div className="row" style={{ marginBottom: 8 }}>
        <b style={{ flex: 1 }}>{PREVIEW_TITEL}</b>
        <button className="btn ghost sm" type="button" onClick={onSluit}>
          Hide
        </button>
      </div>
      <VerzendPreviewInhoud
        items={(w.media?.items ?? []).map((it) => ({
          id: it.id,
          titel: it.title,
          soort: String(it.content_type || '').toLowerCase(),
          url: fileUrl(it.id),
        }))}
        bijschrift={overschrijf || w.tekst}
        betaald={betaaldMedia}
        prijs={w.prijs ?? ''}
        f2f={gekozen?.platform === 'f2f'}
        extra={
          <div className="mut2" style={{ fontSize: 12.5, lineHeight: 1.7 }}>
            <div>{gekozen ? gekozen.label : 'No model chosen yet'}</div>
            {/* 04-10-2026 (Jordy): de prijs ook in de preview, per fan. */}
            {a.metMedia ? (
              <div data-testid="preview-prijs"><b>Price:</b> {a.prijsRegelTekst}</div>
            ) : null}
            <div>{preview ? bereikTekst(preview) : 'Press “Preview count” for the audience size.'}</div>
            {taal && w.vertaal ? (
              <div style={{ color: '#fbbf24' }}>
                {overschrijf
                  ? `Fans receive this ${taal.naam} text (you typed English).`
                  : `Goes out translated to ${taal.naam} — the text below is what you typed. Press “Translate” to see the ${taal.naam}.`}
              </div>
            ) : null}
          </div>
        }
      />
    </div>
  )
}
